import { useState } from 'react'
import { Mail, Phone, MapPin, Send, Clock, Building2, MessageCircle } from 'lucide-react'
import { COMPANY, waLink } from '../config'
import { submitInquiry } from '../data/api'

export function ContactView() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [sent, setSent] = useState(false)
  const [waUrl, setWaUrl] = useState('')
  const [delivered, setDelivered] = useState(false)
  const [sending, setSending] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (sending) return
    setSending(true)

    const url = waLink(
      [
        `New enquiry — ${COMPANY.legalName} website`,
        '',
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Subject: ${form.subject}`,
        '',
        form.message
      ].join('\n')
    )
    setWaUrl(url)

    const result = await submitInquiry({
      type: 'contact',
      name: form.name,
      contact: form.email,
      message: `Subject: ${form.subject}\n\n${form.message}`
    })
    setDelivered(result.delivered)
    if (!result.delivered) window.open(url, '_blank')

    setSending(false)
    setSent(true)
  }

  return (
    <main>
      <section className="relative h-80 bg-brand-navy px-4 sm:px-6 lg:px-8">
        <img
          src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=80"
          alt="Business handshake"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col items-start justify-center text-white">
          <span className="mb-3 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-gold-light">
            Reach Out
          </span>
          <h1 className="font-display text-4xl font-bold sm:text-5xl">Contact us</h1>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink">Get in touch</h2>
            <p className="mt-4 text-brand-slate">Have a question, request or trade idea? At Alpha Import Export, we answer every enquiry within one business day.</p>
            <ul className="mt-8 space-y-5 text-sm text-brand-slate">
              <li className="flex items-start gap-3"><Building2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" /> <span>{COMPANY.legalName}</span></li>
              <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" /> <span>{COMPANY.address}</span></li>
              <li className="flex items-start gap-3"><Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" /> <span>{COMPANY.phone}</span></li>
              <li className="flex items-start gap-3"><Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" /> <span>{COMPANY.email}</span></li>
              <li className="flex items-start gap-3"><Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" /> <span>Mon–Fri 08:30–17:30 CET<br />Sat–Sun closed</span></li>
            </ul>
            <div className="mt-8 rounded-xl border border-brand-slate/10 bg-brand-shell p-5">
              <h3 className="text-sm font-semibold text-brand-ink">Registration details</h3>
              <ul className="mt-3 space-y-1 text-xs text-brand-slate">
                <li>KBO: {COMPANY.kbo}</li>
                <li>EUID: {COMPANY.euid}</li>
                <li>VAT: {COMPANY.vat}</li>
              </ul>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-card sm:p-8 lg:col-span-2">
            {sent ? (
              <div className="py-16 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                  <Send className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-brand-ink">Message sent</h3>
                <p className="mt-2 text-brand-slate">
                  {delivered
                    ? 'Your message has been delivered to our team — we typically reply within one business day.'
                    : 'Your message has been routed to our team on WhatsApp — we typically reply within one business day.'}
                </p>
                {waUrl && (
                  <a href={waUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-95">
                    <MessageCircle className="h-4 w-4" /> {delivered ? 'Chat with us on WhatsApp' : "WhatsApp didn't open? Tap here"}
                  </a>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20" />
                </div>
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Subject</label>
                  <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Message</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full resize-none rounded-lg border border-brand-slate/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-8 py-3 text-sm font-bold text-white transition hover:bg-brand-ocean disabled:opacity-60">
                    <Send className="h-4 w-4" /> {sending ? 'Sending…' : 'Send message'}
                  </button>
                  <p className="mt-2 text-xs text-brand-slate">Sent instantly to our team — no WhatsApp account needed.</p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink">Find us in Bree</h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-brand-slate/10 shadow-card">
            <iframe
              title="Alpha Import & Export B.V. — Bree, Belgium"
              src="https://www.openstreetmap.org/export/embed.html?bbox=5.5469%2C51.0917%2C5.6469%2C51.1917&layer=mapnik&marker=51.1417%2C5.5969"
              className="h-96 w-full border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </main>
  )
}
